import React, { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut } from 'lucide-react';

const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  const [imgPosition, setImgPosition] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const posAtDragStart = useRef({ x: 0, y: 0 });

  // Reset state when opened/closed
  useEffect(() => {
    if (!isOpen) {
      setZoomed(false);
      setImgPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // Escape key closes
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const toggleZoom = useCallback(() => {
    setZoomed(z => {
      if (!z) setImgPosition({ x: 0, y: 0 });
      return !z;
    });
  }, []);

  // Mouse drag when zoomed
  const handlePointerDown = useCallback((e) => {
    if (!zoomed) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    posAtDragStart.current = { ...imgPosition };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [zoomed, imgPosition]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setImgPosition({
      x: posAtDragStart.current.x + dx,
      y: posAtDragStart.current.y + dy,
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Backdrop click closes (only if not zoomed or clicking outside image)
  const handleBackdropClick = useCallback((e) => {
    if (e.target === containerRef.current) {
      if (zoomed) {
        setZoomed(false);
        setImgPosition({ x: 0, y: 0 });
      } else {
        onClose();
      }
    }
  }, [onClose, zoomed]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ animation: 'lightbox-fade-in 200ms ease-out' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90" />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {/* Zoom toggle button */}
      <button
        onClick={toggleZoom}
        className="absolute top-3 left-3 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
      >
        {zoomed ? <ZoomOut size={22} /> : <ZoomIn size={22} />}
      </button>

      {/* Image container */}
      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={handleBackdropClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{ cursor: zoomed ? 'grab' : 'zoom-in', touchAction: zoomed ? 'none' : 'manipulation' }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || 'Full size image'}
          onClick={(e) => { e.stopPropagation(); toggleZoom(); }}
          className="select-none"
          draggable={false}
          style={{
            maxWidth: zoomed ? 'none' : '95vw',
            maxHeight: zoomed ? 'none' : '90vh',
            width: zoomed ? 'auto' : 'auto',
            height: zoomed ? 'auto' : 'auto',
            objectFit: 'contain',
            transform: zoomed
              ? `scale(2) translate(${imgPosition.x / 2}px, ${imgPosition.y / 2}px)`
              : 'scale(1)',
            transition: isDragging.current ? 'none' : 'transform 300ms ease',
          }}
        />
      </div>

      {/* Hint text */}
      {!zoomed && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm pointer-events-none select-none">
          Tap image to zoom &bull; Tap outside to close
        </div>
      )}

      <style>{`
        @keyframes lightbox-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ImageLightbox;
